import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LIBRARY_SORT_OPTIONS } from '../library/librarySort';
import {
  Box,
  Button,
  Checkbox,
  Radio,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { getEntityAuthorDisplayName, getUserDisplayName, getUserInitials } from '../../utils/userUtils';
import { getCategoryChipSx } from '../../designTokens';
import { CONTEXT_FILTER_CATEGORIES, getContextCategoryBadgeSx, getContextCategoryLabel } from '../../constants/contextFolders';
import CapabilityTypeChip from './CapabilityTypeChip';
import ContextCard from '../context-repository/ContextCard';
import LibraryControls from '../library/LibraryControls';
import LibraryResultsTable from '../library/LibraryResultsTable';
import PromptCard from './prompt-display/PromptCard';
import { COMMUNITY_CATEGORIES } from '../../constants/communityCategories';

function sortItems(items, getPrimaryText) {
  return [...items].sort((a, b) =>
    String(getPrimaryText(a) || '').localeCompare(String(getPrimaryText(b) || ''), undefined, {
      sensitivity: 'base',
    })
  );
}

const ATTACH_ROW_NAME_SX = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'text.primary',
  lineHeight: 1.35,
};

const ATTACH_ROW_DESCRIPTION_SX = {
  fontSize: '0.8125rem',
  color: 'text.secondary',
  lineHeight: 1.45,
};

const ATTACH_ROW_BADGE_SX = {
  fontSize: '0.6875rem',
  fontWeight: 500,
  color: 'text.disabled',
  border: '1px solid',
  borderColor: 'rgba(148, 163, 184, 0.35)',
  borderRadius: 3,
  px: 0.75,
  py: 0.125,
  lineHeight: 1.3,
};

const ATTACH_ROW_AUTHOR_AVATAR_SX = {
  width: 20,
  height: 20,
  fontSize: '0.6rem',
  bgcolor: 'rgba(29, 78, 216, 0.12)',
  color: 'primary.main',
};

const ATTACH_ROW_AUTHOR_NAME_SX = {
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'text.secondary',
  lineHeight: 1.3,
  maxWidth: 120,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const ATTACH_CATEGORY_CHIP_SX = {
  flexShrink: 0,
  height: 24,
  maxWidth: 160,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.6875rem',
    px: 1,
  },
};

const KNOWLEDGE_LIBRARY_TABS = [
  { label: 'My Knowledge', value: 0 },
  { label: 'Shared Knowledge', value: 1 },
  { label: 'Favorites', value: 2 },
  { label: 'Recently Used', value: 3 },
];

const KNOWLEDGE_LIBRARY_SCOPES = ['user', 'shared', 'favorites', 'recent'];

const DEFAULT_KNOWLEDGE_SORT_OPTIONS = LIBRARY_SORT_OPTIONS;

const RECENT_KNOWLEDGE_SORT_OPTIONS = [
  ...DEFAULT_KNOWLEDGE_SORT_OPTIONS,
];

function formatSelectionCount(count, unitLabel) {
  if (unitLabel === 'knowledge') return `${count} knowledge ${count === 1 ? 'item' : 'items'} selected`;
  if (!unitLabel) {
    return count === 1 ? '1 selected' : `${count} selected`;
  }
  const unit = count === 1 ? unitLabel : unitLabel === 'capability' ? 'capabilities' : `${unitLabel}s`;
  return `${count} ${unit} selected`;
}

export default function AttachExistingSelector({
  open,
  onClose,
  title,
  description,
  items = [],
  selectedIds = [],
  pinnedItems = [],
  loading: externalLoading = false,
  onFetchDefaultItems,
  onSearchItems,
  searchPlaceholder = 'Search...',
  emptyText = 'No matching items.',
  attachedSectionTitle = 'Already attached',
  availableSectionTitle = 'Available to add',
  getPrimaryText = (item) => item?.name || item?.title || 'Untitled',
  getSecondaryText = (item) => item?.description || '',
  getTags = (item) => item?.tags || [],
  /** 'context' shows knowledge category badge; 'prompt' shows capability category + type chip */
  itemKind = 'context',
  itemIcon = <DescriptionIcon />,
  renderAvatar,
  onPreview,
  createAction = null,
  confirmLabel,
  confirmAllSelected = false,
  /** Optional unit for selection count, e.g. "context" → "3 contexts selected" */
  selectionUnitLabel = null,
  singleSelection = false,
  /** Render a selectable Knowledge Library surface instead of the compact list. */
  libraryMode = false,
  onConfirm,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileLibrary = libraryMode && isMobile;
  const serverMode = Boolean(onFetchDefaultItems && onSearchItems);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [draftSelectedIds, setDraftSelectedIds] = useState([]);
  const [defaultItems, setDefaultItems] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [libraryTab, setLibraryTab] = useState(0);
  const [librarySort, setLibrarySort] = useState({ key: 'popular', direction: 'desc' });
  const [viewMode, setViewMode] = useState('grid');
  const defaultCacheRef = useRef([]);
  const selectedItemCacheRef = useRef(new Map());

  const libraryScope = KNOWLEDGE_LIBRARY_SCOPES[libraryTab] || 'user';
  const libraryRequestOptions = useMemo(() => ({
    scope: libraryScope,
    category: categoryFilter,
    sortBy: librarySort.key,
    sortDirection: librarySort.direction,
  }), [categoryFilter, libraryScope, librarySort]);
  const requestOptions = libraryMode ? libraryRequestOptions : undefined;
  const librarySortOptions = itemKind === 'prompt'
    ? [...(libraryTab === 3 ? [{ key: 'lastUsed', label: 'Last Used' }] : []), { key: 'recent', label: 'Recently Added' }, { key: 'popular', label: 'Relevant' }, { key: 'usage', label: 'Most Used' }]
    : libraryTab === 3
    ? RECENT_KNOWLEDGE_SORT_OPTIONS
    : DEFAULT_KNOWLEDGE_SORT_OPTIONS;

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setDebouncedSearch('');
    setCategoryFilter(libraryMode ? 'all' : 'All');
    setLibraryTab(0);
    setLibrarySort({ key: 'popular', direction: 'desc' });
    setDraftSelectedIds(Array.isArray(selectedIds) ? (singleSelection ? selectedIds.slice(0, 1) : selectedIds) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [open, search]);

  useEffect(() => {
    if (!open || !serverMode || debouncedSearch.trim()) return undefined;
    let cancelled = false;

    const loadDefault = async () => {
      setListLoading(true);
      try {
        const list = await onFetchDefaultItems(requestOptions);
        if (cancelled) return;
        defaultCacheRef.current = Array.isArray(list) ? list : [];
        setDefaultItems(defaultCacheRef.current);
        setSearchResults([]);
      } catch {
        if (!cancelled) {
          defaultCacheRef.current = [];
          setDefaultItems([]);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    };

    loadDefault();
    return () => {
      cancelled = true;
    };
  }, [open, serverMode, onFetchDefaultItems, requestOptions, debouncedSearch]);

  useEffect(() => {
    if (!open || !serverMode) return undefined;
    const query = debouncedSearch.trim();
    if (!query) {
      setSearchResults([]);
      setDefaultItems(defaultCacheRef.current);
      return undefined;
    }

    let cancelled = false;
    setListLoading(true);
    onSearchItems(query, requestOptions)
      .then((list) => {
        if (!cancelled) setSearchResults(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, serverMode, debouncedSearch, onSearchItems, requestOptions]);

  const clientFilteredItems = useMemo(() => {
    if (serverMode) return [];
    const q = search.trim().toLowerCase();
    return (items || []).filter((item) => {
      if (!item?.id) return false;
      if (!q) return true;
      const primary = String(getPrimaryText(item) || '').toLowerCase();
      const secondary = String(getSecondaryText(item) || '').toLowerCase();
      return primary.includes(q) || secondary.includes(q);
    });
  }, [getPrimaryText, getSecondaryText, items, search, serverMode]);

  const availableItems = useMemo(() => {
    if (serverMode) {
      const source = debouncedSearch.trim() ? searchResults : defaultItems;
      return libraryMode ? source : sortItems(source, getPrimaryText);
    }
    return sortItems(clientFilteredItems, getPrimaryText);
  }, [serverMode, debouncedSearch, searchResults, defaultItems, clientFilteredItems, getPrimaryText, libraryMode]);

  const attachedItems = useMemo(() => {
    const idSet = new Set((selectedIds || []).filter(Boolean));
    if (idSet.size === 0) return [];

    const fromPinned = (pinnedItems || []).filter((item) => item?.id && idSet.has(item.id));
    const pinnedIds = new Set(fromPinned.map((item) => item.id));
    const fromAvailable = availableItems.filter((item) => idSet.has(item.id) && !pinnedIds.has(item.id));
    const fromClient = serverMode
      ? []
      : (items || []).filter((item) => item?.id && idSet.has(item.id) && !pinnedIds.has(item.id));

    const merged = mergeUniqueById([...fromPinned, ...fromAvailable, ...fromClient]);
    return sortItems(merged, getPrimaryText);
  }, [selectedIds, pinnedItems, availableItems, items, serverMode, getPrimaryText]);

  const attachedIdSet = useMemo(() => new Set(attachedItems.map((item) => item.id)), [attachedItems]);

  const availableToAdd = useMemo(
    () => mergeUniqueById([...availableItems, ...(singleSelection && !debouncedSearch.trim() ? pinnedItems : [])]).filter((item) => !attachedIdSet.has(item.id)),
    [availableItems, attachedIdSet, singleSelection, debouncedSearch, pinnedItems]
  );

  const orderedAvailableToAdd = useMemo(() => {
    const selectedIdSet = new Set(draftSelectedIds);
    return [...availableToAdd].sort((a, b) => {
      const aSelected = selectedIdSet.has(a.id);
      const bSelected = selectedIdSet.has(b.id);
      return Number(bSelected) - Number(aSelected);
    });
  }, [availableToAdd, draftSelectedIds]);

  const itemById = useMemo(() => {
    const map = new Map();
    [...(pinnedItems || []), ...attachedItems, ...availableItems, ...(items || [])].forEach((item) => {
      if (item?.id) map.set(item.id, item);
    });
    return map;
  }, [pinnedItems, attachedItems, availableItems, items]);

  const selectedLibraryItems = useMemo(
    () => draftSelectedIds
      .map((id) => itemById.get(id) || selectedItemCacheRef.current.get(id))
      .filter(Boolean),
    [draftSelectedIds, itemById]
  );

  useEffect(() => {
    [...(pinnedItems || []), ...attachedItems, ...availableItems, ...(items || [])].forEach((item) => {
      if (item?.id) selectedItemCacheRef.current.set(item.id, item);
    });
  }, [attachedItems, availableItems, items, pinnedItems]);

  const libraryItems = useMemo(
    () => mergeUniqueById([
      ...selectedLibraryItems,
      ...availableItems,
      ...(!search.trim() && libraryTab === 0 ? pinnedItems : []),
    ]),
    [selectedLibraryItems, pinnedItems, availableItems, search, libraryTab]
  );

  const libraryVisibleItems = useMemo(() => {
    const selectedIdSet = new Set(draftSelectedIds);
    const categories = (Array.isArray(categoryFilter) ? categoryFilter : categoryFilter === 'all' ? [] : [categoryFilter]).map((value) => value.toLowerCase());
    const visibleItems = libraryItems.filter((item) => selectedIdSet.has(item.id) || !categories.length || categories.includes(String(itemKind === 'prompt' ? item.category || 'General' : getContextCategoryLabel(item)).toLowerCase()));

    return [...visibleItems].sort((a, b) => (
      Number(selectedIdSet.has(b.id)) - Number(selectedIdSet.has(a.id))
    ));
  }, [categoryFilter, draftSelectedIds, libraryItems, itemKind]);

  const handleLibraryTabChange = (_event, tab) => {
    setLibraryTab(tab);
    setLibrarySort({ key: tab === 3 ? 'lastUsed' : 'recent', direction: 'desc' });
  };

  const showAttachedSection = attachedItems.length > 0 && (confirmAllSelected || selectedIds.length > 0);

  const toggleItem = (id) => {
    setDraftSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((v) => v !== id) : singleSelection ? [id] : [...prev, id]
    ));
  };

  const handleConfirm = async () => {
    const existing = new Set(selectedIds || []);
    const additions = draftSelectedIds.filter((id) => !existing.has(id));
    if (!confirmAllSelected && additions.length === 0) {
      onClose();
      return;
    }
    await onConfirm(
      additions,
      draftSelectedIds,
      draftSelectedIds
        .map((id) => itemById.get(id) || selectedItemCacheRef.current.get(id))
        .filter(Boolean)
    );
  };

  const existingIds = new Set(selectedIds || []);
  const addedCount = draftSelectedIds.filter((id) => !existingIds.has(id)).length;
  const removedCount = (selectedIds || []).filter((id) => !draftSelectedIds.includes(id)).length;
  const resolvedConfirmLabel = typeof confirmLabel === 'function'
    ? confirmLabel({
        selectedCount: draftSelectedIds.length,
        addedCount,
        removedCount,
      })
    : confirmLabel;

  const resolveAuthorUser = (item) => {
    if (!item) return null;
    const displayName = getEntityAuthorDisplayName(item);
    if (displayName === 'Unknown User') return null;
    const email = item.userEmail || item.author?.email || item.email;
    const id = item.userId || item.authorId || item.author?.id;
    return { name: displayName, email, id, userId: id };
  };

  const renderCategoryChip = (item) => {
    if (renderAvatar) return null;

    const categoryLabel = itemKind === 'prompt'
      ? (String(item?.category || '').trim() || 'General')
      : getContextCategoryLabel(item);
    const chipSx = itemKind === 'prompt'
      ? getCategoryChipSx(categoryLabel)
      : getContextCategoryBadgeSx(categoryLabel);

    return (
      <Chip
        label={categoryLabel}
        size="small"
        variant="outlined"
        sx={{ ...chipSx, ...ATTACH_CATEGORY_CHIP_SX }}
      />
    );
  };

  const renderItemRow = useCallback((item) => {
    const checked = draftSelectedIds.includes(item.id);
    const tags = getTags(item);
    const authorUser = resolveAuthorUser(item);
    const authorName = authorUser ? getUserDisplayName(authorUser) : null;
    const title = getPrimaryText(item);
    return (
      <ListItem
        key={item.id}
        disablePadding
        secondaryAction={onPreview ? (
          <Tooltip title="Preview">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onPreview(item);
              }}
              sx={{ mr: 1 }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      >
        <ListItemButton onClick={() => toggleItem(item.id)} selected={checked}>
          {React.createElement(singleSelection ? Radio : Checkbox, {
            inputProps: { 'aria-label': `Select ${title}` },
            edge: 'start', checked, tabIndex: -1, disableRipple: true,
            onClick: (event) => event.stopPropagation(),
            onChange: () => toggleItem(item.id), sx: { mr: 2 },
          })}
          <ListItemText
            primary={
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 0,
                  maxWidth: '100%',
                  flexWrap: 'nowrap',
                }}
              >
                {renderAvatar?.(item)}
                <Typography
                  component="span"
                  sx={{
                    ...ATTACH_ROW_NAME_SX,
                    flex: '0 1 auto',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {title}
                </Typography>
                {renderCategoryChip(item)}
              </Box>
            }
            primaryTypographyProps={{ component: 'div' }}
            secondary={
              <Box component="span">
                <Typography
                  component="div"
                  sx={{
                    ...ATTACH_ROW_DESCRIPTION_SX,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {getSecondaryText(item) || 'No description'}
                </Typography>
                {(authorName || (Array.isArray(tags) && tags.length > 0) || itemKind === 'prompt') && (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 0.625, rowGap: 0.5 }}
                  >
                    {authorName && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          minWidth: 0,
                          flexShrink: 0,
                        }}
                      >
                        <Avatar sx={ATTACH_ROW_AUTHOR_AVATAR_SX}>
                          {getUserInitials(authorUser)}
                        </Avatar>
                        <Typography component="span" sx={ATTACH_ROW_AUTHOR_NAME_SX}>
                          {authorName}
                        </Typography>
                      </Box>
                    )}
                    {Array.isArray(tags) && tags.slice(0, 2).map((tag, i) => (
                      <Box key={`${tag}-${i}`} component="span" sx={ATTACH_ROW_BADGE_SX}>
                        {tag}
                      </Box>
                    ))}
                    {itemKind === 'prompt' && (
                      <Typography variant="caption" color="text.secondary">{({ 1: 'Minimally defined', 2: 'Partly defined', 3: 'Well defined' })[item.buildQuality?.tier] || 'Not assessed'}</Typography>
                    )}
                    {itemKind === 'prompt' && (
                      <CapabilityTypeChip
                        capabilityType={item.capabilityType}
                        sx={{ height: 20, fontSize: '0.6875rem' }}
                      />
                    )}
                  </Stack>
                )}
              </Box>
            }
            secondaryTypographyProps={{ component: 'div' }}
          />
        </ListItemButton>
      </ListItem>
    );
  }, [draftSelectedIds, getPrimaryText, getSecondaryText, getTags, itemKind, onPreview, renderAvatar]);

  const isLoading = externalLoading || listLoading;
  const hasListContent = libraryMode
    ? libraryVisibleItems.length > 0
    : (showAttachedSection || orderedAvailableToAdd.length > 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={libraryMode ? false : "xl"}
      fullWidth
      fullScreen={isMobileLibrary}
      PaperProps={libraryMode ? {
        sx: {
          width: { xs: '100vw', sm: 'min(96vw, 1540px)' },
          maxWidth: 'none',
          height: { xs: '100dvh', sm: 'min(88vh, 1000px)' },
          borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      } : undefined}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">{title}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      {description && <Typography color="text.secondary" sx={{ px: 3, pb: 2, fontSize: '0.875rem', display: libraryMode ? { xs: 'none', sm: 'block' } : 'block' }}>{description}</Typography>}
      <DialogContent sx={libraryMode ? {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        pt: '12px !important',
        px: { xs: 2, sm: 3 },
      } : undefined}>
        {!libraryMode && (
          <TextField
            fullWidth
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        )}
        {libraryMode ? (
          <>
            <LibraryControls
              searchTerm={search}
              onSearchChange={(event) => setSearch(event.target.value)}
              searchPlaceholder={searchPlaceholder}
              selectedCategory={categoryFilter}
              onCategorySelect={setCategoryFilter}
              searchPending={isLoading || search !== debouncedSearch}
              categories={itemKind === 'prompt' ? [...new Set([...COMMUNITY_CATEGORIES, ...availableItems.map((item) => item.category).filter(Boolean)])] : CONTEXT_FILTER_CATEGORIES}
              activeTab={libraryTab}
              onTabChange={handleLibraryTabChange}
              tabs={itemKind === 'prompt' ? [{ label: 'My Capabilities', value: 0 }, { label: 'Shared', value: 1 }, { label: 'Favorites', value: 2 }, { label: 'Recently Used', value: 3 }] : KNOWLEDGE_LIBRARY_TABS}
              compactControls={false}
              showViewToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              relevanceSearch={libraryTab === 0 || libraryTab === 1}
              showSharedScopeFilters={false}
              currentSort={librarySort}
              onSortChange={setLibrarySort}
              sortOptions={librarySortOptions}
            />
            {isLoading && !hasListContent ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : !hasListContent ? (
              <Alert severity="info">
                {search.trim() ? `No ${itemKind === 'prompt' ? 'capabilities' : 'knowledge'} found matching "${search.trim()}"` : emptyText}
              </Alert>
            ) : (
              <Box sx={{ position: 'relative', flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={22} />
                  </Box>
                )}
                {viewMode === 'list' ? <LibraryResultsTable kind={itemKind === 'prompt' ? 'capabilities' : 'knowledge'} preferenceKey={`picker-${itemKind}`} items={libraryVisibleItems} scope={libraryTab} onOpen={onPreview || ((item) => toggleItem(item.id))} selectable selectedIds={draftSelectedIds} singleSelection={singleSelection} onSelect={(item) => toggleItem(item.id)} /> : <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1.5,
                    pb: 0.5,
                    minWidth: 0,
                  }}
                >
                  {libraryVisibleItems.map((item) => (
                    itemKind === 'prompt' ? <PromptCard key={item.id} prompt={item} compact selectable selected={draftSelectedIds.includes(item.id)} singleSelection={singleSelection} onSelect={(selectedItem) => toggleItem(selectedItem.id)} onPromptClick={(selectedItem) => toggleItem(selectedItem.id)} hideActions accessibleLabel={`Select ${getPrimaryText(item)}`} /> : <ContextCard
                      key={item.id}
                      context={item}
                      compact
                      selectable
                      selected={draftSelectedIds.includes(item.id)}
                      onSelect={(selectedItem) => toggleItem(selectedItem.id)}
                      onContextClick={onPreview ? onPreview : () => toggleItem(item.id)}
                      fullWidthOnMobile
                    />
                  ))}
                </Box>}
              </Box>
            )}
          </>
        ) : isLoading && !hasListContent ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : !hasListContent ? (
          <Alert severity="info">
            {search.trim() ? `No items found matching "${search.trim()}"` : emptyText}
          </Alert>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {showAttachedSection && (
                <>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ px: 2, py: 1, fontWeight: 700 }}
                  >
                    {attachedSectionTitle}
                  </Typography>
                  {attachedItems.map(renderItemRow)}
                </>
              )}
              {orderedAvailableToAdd.length > 0 && (
                <>
                  {showAttachedSection && (
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ px: 2, py: 1, pt: 1.5, fontWeight: 700 }}
                    >
                      {availableSectionTitle}
                    </Typography>
                  )}
                  {orderedAvailableToAdd.map(renderItemRow)}
                </>
              )}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{
        borderTop: 1,
        borderColor: 'divider',
        p: { xs: 1.5, sm: 2 },
        gap: isMobileLibrary ? 1 : 0,
      }}>
        {!isMobileLibrary && <Button onClick={onClose}>Cancel</Button>}
        {!isMobileLibrary && <Box sx={{ flex: 1 }} />}
        {createAction && (
          <Box sx={isMobileLibrary ? {
            flex: 1,
            minWidth: 0,
            '& .MuiButton-root': { width: '100%', minHeight: 44, whiteSpace: 'nowrap' },
          } : undefined}>
            {createAction}
          </Box>
        )}
        {!isMobileLibrary && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ alignSelf: 'center', mr: 1.5, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
          >
            {formatSelectionCount(draftSelectedIds.length, selectionUnitLabel)}
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
          sx={isMobileLibrary ? { flex: 1, minWidth: 0, minHeight: 44, whiteSpace: 'nowrap' } : undefined}
        >
          {isMobileLibrary && itemKind === 'context'
            ? 'Apply Knowledge'
            : (resolvedConfirmLabel || `Attach ${draftSelectedIds.length} selected`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function mergeUniqueById(lists) {
  const map = new Map();
  lists.forEach((item) => {
    if (item?.id && !map.has(item.id)) map.set(item.id, item);
  });
  return [...map.values()];
}
